import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";

import { FormField } from "components/form/FormField/FormField";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { Select } from "components/ui/Select/Select";
import { isOutOfScheduleProgrammeOffered } from "features/immunizations/utils/vaccinationProgramme";

import style from "../AddVaccineForm.module.css";
import {
  DOSE_NUMBER_OPTIONS,
  INFLUENZA_COURSE_CODES,
  INFLUENZA_SINGLE_DOSE_FROM_AGE_YEARS,
  PPV23_CODE,
  PPV23_SINGLE_DOSE_WINDOW_DAYS,
  SINGLE_DOSE_COURSE_CODES,
  SINGLE_DOSE_NUMBER_OPTIONS,
  VACCINE_TYPE_GROUPS,
} from "../constants";
import { DateField } from "./DateField";
import { GivenElsewhereFields } from "./GivenElsewhereFields";
import { LockedOrSelectField } from "./LockedOrSelectField";
import { TextField } from "./TextField";
import { VaccinationProgrammeFields } from "./VaccinationProgrammeFields";
import { VACCINE_MODE, VaccineModeSwitch } from "./VaccineModeSwitch";


// "Date given" is capped at today in both modes. This flag lifts that
// cap so future dates can be entered — needed for NIIS overage certification
// cases (e.g. a dose dated when the child is 740 days old, beyond today). Off by
// default; enable only in test/cert environments via .env.
const ALLOW_FUTURE_VACCINE_DATES =
  process.env.REACT_APP_ALLOW_FUTURE_VACCINE_DATES === "true";

// Whole years completed by `onDate`. Returns -1 when either date is missing, so an
// unknown age never reads as old enough — the sequence positions stay on offer and the
// backend has the last word rather than the form guessing.
/**
 * Whether the patient already has a dose of `vaccineCode` inside `windowDays` of
 * `onDate`, ignoring the record being edited.
 *
 * Mirrors the backend: the most recent prior dose is the one compared. Only doses
 * before `onDate` count — the backend's day count is signed, so a later dose would
 * satisfy its comparison too, which reads as an artefact rather than the rule.
 */
const hasPriorDoseWithin = (
  history,
  vaccineCode,
  onDate,
  windowDays,
  excludedId
) => {
  if (!Array.isArray(history) || !onDate) return false;

  const givenOn = dayjs(onDate);
  const priorDates = history
    .filter(
      (record) =>
        record?.vaccineCode === vaccineCode &&
        !!record?.dateGiven &&
        String(record?.id) !== String(excludedId)
    )
    .map((record) => dayjs(record.dateGiven))
    .filter((date) => date.isValid() && !date.isAfter(givenOn));

  if (priorDates.length === 0) return false;

  const mostRecent = priorDates.reduce((latest, date) =>
    date.isAfter(latest) ? date : latest
  );

  return givenOn.diff(mostRecent, "day") < windowDays;
};

const getAgeInYearsAt = (dateOfBirth, onDate) => {
  if (!dateOfBirth || !onDate) return -1;

  const age = dayjs(onDate).diff(dayjs(dateOfBirth), "year");

  return Number.isFinite(age) ? age : -1;
};

export const VaccineSelectionSection = ({
  healthlinkVaccines,
  isLoadingHealthlink,
  onVaccineSelect,
  mode,
  onModeChange,
  showPatientSelect = false,
  selectedPatient,
  onPatientChange,
  patientError,
  isEditMode,
  healthlinkEnabled = true,
  patientDateOfBirth,
  immunisationHistory,
  editedImmunisationId,
  prefillVaccineCodeId,
}) => {
  const { watch, setValue } = useFormContext();

  const vaccineCodeId = watch("vaccineCodeId");
  const dateGiven = watch("dateGiven");
  const vaccineType = watch("type");
  const doseNumberCode = watch("doseNumberCode");

  // Opening the form from a due dose on the schedule (KIN-1842). Runs the same handler the
  // picker runs rather than seeding form values directly: every derived field -- type,
  // SNOMED, MVX, manufacturer, dose -- is set inside that handler, so a value written
  // straight into the form defaults would leave the record half-built and fail at
  // submission.
  //
  // Once only, and never over a choice already made: the guard is the field being empty.
  useEffect(() => {
    if (!prefillVaccineCodeId || !healthlinkVaccines?.length || vaccineCodeId) return;

    const product = healthlinkVaccines.find(
      (vaccine) => String(vaccine.id) === String(prefillVaccineCodeId)
    );

    if (product) onVaccineSelect(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillVaccineCodeId, healthlinkVaccines]);

  const isHealthlink = mode === VACCINE_MODE.HEALTHLINK;

  // A single-dose course has no sequence, so offering the sequence positions asks the
  // clinician to describe one that does not exist — which is how a real RSV submission
  // went out as "Primary Course Additional Dose" (KIN-1708). Such a course is offered
  // "Single dose" alone, and sends 1, the value every single-dose sample in the
  // specification carries.
  const selectedProduct = healthlinkVaccines.find(
    (product) => String(product.id) === String(vaccineCodeId)
  );
  // Influenza is a single-dose course from age nine at the date given, so whether the
  // sequence positions apply depends on the patient as well as the product. The backend
  // gates its HARD_STOP on the same rule, so a form that decided on the product alone
  // would offer an adult's annual influenza dose a sequence the backend then rejects.
  //
  const isInfluenzaSingleDose =
    !!selectedProduct &&
    INFLUENZA_COURSE_CODES.includes(selectedProduct.code) &&
    getAgeInYearsAt(patientDateOfBirth, dateGiven) >=
      INFLUENZA_SINGLE_DOSE_FROM_AGE_YEARS;
  // PPV23 is a single-dose course when the patient's previous PPV23 dose is inside the
  // five-year window, so this one depends on their history.
  //
  // The record being edited is excluded, or reopening a PPV23 record would compare it
  // against itself and always narrow. Where the history has not loaded, or holds no
  // prior dose, the sequence positions stay on offer: over-narrowing would stop a
  // clinician recording a dose the backend would have accepted, while under-narrowing
  // leaves them a HARD_STOP they can satisfy by choosing dose 1.
  const isPpv23SingleDose =
    selectedProduct?.code === PPV23_CODE &&
    hasPriorDoseWithin(
      immunisationHistory,
      PPV23_CODE,
      dateGiven,
      PPV23_SINGLE_DOSE_WINDOW_DAYS,
      editedImmunisationId
    );
  const isSingleDoseCourse =
    !!selectedProduct &&
    (SINGLE_DOSE_COURSE_CODES.includes(selectedProduct.code) ||
      isInfluenzaSingleDose ||
      isPpv23SingleDose);
  const doseNumberOptions = isSingleDoseCourse
    ? SINGLE_DOSE_NUMBER_OPTIONS
    : DOSE_NUMBER_OPTIONS;

  // Narrowing the options is not enough on its own: a dose number carried over from
  // another product would sit in the field without being offered by its own dropdown,
  // and the backend rejects any value but 1 for these courses.
  useEffect(() => {
    if (isSingleDoseCourse && doseNumberCode !== "1") {
      setValue("doseNumberCode", "1", { shouldValidate: true });
    }
  }, [isSingleDoseCourse, doseNumberCode, setValue]);
  const fieldCount = (showPatientSelect ? 1 : 0) + 3 + (isHealthlink ? 1 : 0);
  const gridClass =
    fieldCount === 5
      ? style.inputRowFive
      : fieldCount === 4
      ? style.inputRow
      : style.inputRowThree;

  return (
    <>
      {/* The switch stays whenever the practice uses HealthLink at all, even when this patient's
          record cannot be submitted yet: the GP may still open HealthLink mode, read why it is
          refused, and fix it. Save is what is withheld, not the mode. */}
      {!!healthlinkEnabled && (
        <VaccineModeSwitch mode={mode} onChange={onModeChange} />
      )}

      <div className={gridClass}>
        {showPatientSelect && (
          <div className={style.fieldWrapper}>
            <label className={style.pregnancyFormLabel}>Patient</label>
            <PatientAutocomplete
              id="add-vaccine-patient-select"
              value={selectedPatient}
              onChange={onPatientChange}
              placeholder="Search patient..."
              inputSx={
                patientError
                  ? {
                      "& .MuiOutlinedInput-root fieldset": {
                        borderColor: "#dc2626",
                      },
                    }
                  : undefined
              }
            />
            {patientError && (
              <span
                style={{
                  fontFamily: "Nunito, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#dc2626",
                  marginTop: "4px",
                }}
              >
                {patientError}
              </span>
            )}
          </div>
        )}

        {mode === VACCINE_MODE.HEALTHLINK ? (
          isEditMode && vaccineCodeId ? (
            <TextField name="vaccineName" label="Name" disabled required />
          ) : (
            <FormField name="vaccineName">
              <label className={style.pregnancyFormLabel}>Name *</label>
              <Select
                name="vaccineName"
                {...(isLoadingHealthlink
                  ? {
                      options: [
                        {
                          label: "Loading vaccines...",
                          value: "",
                          disabled: true,
                        },
                      ],
                    }
                  : {
                      groups: Object.entries(
                        healthlinkVaccines.reduce((acc, vaccine) => {
                          const group = vaccine.vaccineType || "Other";
                          if (!acc[group]) acc[group] = [];
                          acc[group].push({
                            label: vaccine.name,
                            value: String(vaccine.id),
                          });
                          return acc;
                        }, {})
                      )
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([label, options]) => ({
                          label,
                          options: options.sort((a, b) =>
                            a.label.localeCompare(b.label)
                          ),
                        })),
                    })}
                value={vaccineCodeId ? String(vaccineCodeId) : ""}
                onChange={(value) => {
                  const selected = healthlinkVaccines.find(
                    (v) => v.id.toString() === value
                  );
                  onVaccineSelect(selected || null);
                }}
                placeholder="Select a vaccine"
                fullWidth
                ariaRequired
              />
            </FormField>
          )
        ) : (
          <TextField
            name="vaccineName"
            label="Name"
            placeholder="Enter vaccine name"
            required
          />
        )}

        {mode === VACCINE_MODE.HEALTHLINK ? (
          <TextField
            name="type"
            label="Type"
            value={vaccineType || ""}
            disabled
            placeholder="Auto-filled from vaccine"
          />
        ) : (
          <FormField name="type">
            <label className={style.pregnancyFormLabel}>Type</label>
            <Select
              name="type"
              groups={VACCINE_TYPE_GROUPS}
              value={vaccineType || ""}
              onChange={(value) =>
                setValue("type", value, { shouldValidate: true })
              }
              placeholder="Select type"
              fullWidth
            />
          </FormField>
        )}

        <DateField
          name="dateGiven"
          label="Date given"
          placeholder="Select date given"
          // A dose cannot precede the patient's birth. The backend refuses one since
          // KIN-1728, so without this the clinician meets a rejection after filling the
          // whole form instead of a date they cannot pick (KIN-1729). Applied in manual
          // mode too: a historical dose given abroad still cannot predate the patient.
          // CustomCalendar checks typed input against minDate as well as the picker, so
          // this covers both ways in.
          minDate={patientDateOfBirth || undefined}
          // Nor can a dose be dated after today — it has not been given yet. The
          // backend refuses one at save time for manual entries as well as HealthLink
          // ones, so capping only in HealthLink mode left a manual record to be
          // rejected with a 409 after the form was filled in.
          maxDate={ALLOW_FUTURE_VACCINE_DATES ? undefined : new Date()}
          required
        />

        {/* MMR can be given outside the childhood schedule — for travel, or during an
            outbreak — and that dose must not consume the scheduled one (KIN-1875). */}
        {!!isOutOfScheduleProgrammeOffered(vaccineType) && (
          <VaccinationProgrammeFields dateGiven={dateGiven} />
        )}

        {mode === VACCINE_MODE.HEALTHLINK && (
          <LockedOrSelectField
            name="doseNumberCode"
            label="Dose Number"
            options={doseNumberOptions}
            placeholder="Select"
            required
          />
        )}

        {/* Last in the row, so it starts a line of its own beneath the four fields above
            rather than displacing Dose Number out of the top row. Ticking it is what stops
            the administration fields being demanded (KIN-1883). */}
        <GivenElsewhereFields />

        <HiddenVaccineFields />
      </div>
    </>
  );
};

const HiddenVaccineFields = () => {
  const { register } = useFormContext();
  return (
    <div style={{ display: "none" }}>
      <input type="hidden" {...register("vaccineCodeName")} />
      <input type="hidden" {...register("vaccineCodeCvxCode")} />
      <input type="hidden" {...register("vaccineCodeMvxCode")} />
    </div>
  );
};
